import React from "react";
import { Typography, Box, Button, Paper } from "@mui/material";
import { ArchivedSection, ArchivedHeader } from "../styled";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { MatchingRound } from "../types";

interface ArchivedExportsProps {
  showArchived: boolean;
  toggleArchived: () => void;
  archivedMatchingRounds: MatchingRound[];
  onSelectArchivedRound: (roundId: number) => void;
}

export const ArchivedExports: React.FC<ArchivedExportsProps> = ({
  showArchived,
  toggleArchived,
  archivedMatchingRounds,
  onSelectArchivedRound
}) => {
  const renderArchivedExports = () => {
    if (archivedMatchingRounds.length === 0) {
      return (
        <Typography variant="body1">
          No archived matching rounds found.
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        {archivedMatchingRounds.map((round) => {
          const hasExports = round.hasExports === true;
          return (
            <Paper
              key={round.id}
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '12px',
                border: '1px solid #e8e8e8',
                display: 'flex',
                flexDirection: 'column',
                minWidth: '280px',
                maxWidth: '340px',
                gap: 2,
                opacity: hasExports ? 1 : 0.5
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: hasExports ? '#1e3a5f' : '#9e9e9e',
                  lineHeight: 1.4
                }}
              >
                {round.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#757575'
                }}
              >
                {round.status}
                {!hasExports && ' • No exports'}
              </Typography>
              <Button
                variant="contained"
                onClick={() => onSelectArchivedRound(round.id)}
                disabled={!hasExports}
                sx={{
                  bgcolor: hasExports ? '#2167e2' : '#bdbdbd',
                  '&:hover': { bgcolor: hasExports ? '#1a52b3' : '#bdbdbd' },
                  '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '14px',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  alignSelf: 'flex-start'
                }}
              >
                Select
              </Button>
            </Paper>
          );
        })}
      </Box>
    );
  };

  return (
    <ArchivedSection>
      <ArchivedHeader
        onClick={toggleArchived}
        className={showArchived ? "expanded" : ""}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Archived Matching Rounds</Typography>
        <ExpandMoreIcon fontSize="small" />
      </ArchivedHeader>

      {showArchived && renderArchivedExports()}
    </ArchivedSection>
  );
};
